import { useMemo } from "react";
import { LineItemElement, LineItemSearchResult, RuleInputLineItem } from "@rebuild/shared";
import { InputFieldProps } from "./types";
import { FilterableSelect, MultiSelect } from "../GenericInputComponents";
import { LineItemPermutationSearch } from "./LineItemPermutationSearch";
import { PermutationCatSelSelect } from "./PermutationCatSelSelect";
import LoadingAnimation from "../../../components/ui/utils/LoadingAnimation";
import { useLineItemTemplates } from "../../../hooks/useLineItemTemplates";
import { useLineItemPermutationCatSels } from "../../../hooks/useLineItemPermutationCatSels";
import { getAllValidPermutations } from "../../../utils/getAllValidPermutations";
import { IDatabaseLineItem } from "../../CreatorTools/LineItemDBCreationTool/types";

type IndexToSelectedValues = Record<string, { values: string[]; isInherited: boolean }>;
type IndexToSelectedSingleValue = Record<string, { value: string; isInherited: boolean }>;

type LineItemUpdate = (value: Partial<RuleInputLineItem> | undefined) => void;

interface LineItemVariationProps {
  selectedDbLineItem: IDatabaseLineItem;
  inputValue: Partial<RuleInputLineItem> | undefined;
  sourceLineItem: Partial<RuleInputLineItem> | undefined;
  canBeDifferentFromSource: boolean;
  onUpdate: LineItemUpdate;
}

const getByPath = (obj: any, path: string): any =>
  path
    .split(/\.|\[|\]/)
    .filter(Boolean)
    .reduce((acc, key) => acc?.[key], obj);

/**
 * This component is very much a work in process.
 *
 * The goal of this component is for the user to set the parameters that will be used to select which of an estimate's line items a rule will run
 * on. These parameters are the line item id and either an exact permutation or partial permutation. The specific parameters that a user is allowed
 * to set is determined by the input field's parameters.
 *
 * This top-level component is responsible for selecting the line item template (via its original text). Depending on the input field's parameters, it
 * then delegates to one of three variation components:
 * - {@link PermutationSelectInput}: select a specific permutation
 * - {@link PartialSelectionInput}: select a list of acceptable values per element (partial permutation)
 * - {@link VariableOverrideInput}: select a single value per element (variable override)
 *
 * We also have the concept of a "source line item". This essentially means that this line item inherits all of the properties set by the source line
 * item (the user can only modify the parameters tha aren't already set by this source line item)
 */
export const LineItemInput: React.FC<InputFieldProps> = ({ objectInput, inputField, inputValue: _inputValue, onUpdate: _onUpdate }) => {
  if (inputField.type !== "lineItem") {
    return <>non line item types are currently not supported</>;
  }

  // input values aren't typed, but we basically assert that this line item field's value is of the type LineItemInputValue
  const inputValue: Partial<RuleInputLineItem> | undefined = _inputValue;

  const { dbLineItems, isLoading: isDbLineItemsLoading } = useLineItemTemplates();

  const { selectedDbLineItem, sourceLineItem } = useMemo(() => {
    const sourceLineItem = inputField.parameters?.sourceLineItemPath
      ? (getByPath(objectInput, inputField.parameters.sourceLineItemPath) as Partial<RuleInputLineItem>)
      : undefined;
    const _lineItemId =
      !!inputField.parameters?.sourceLineItemPath && !inputField.parameters.canBeDifferentFromSource
        ? sourceLineItem?.lineItemId
        : inputValue?.lineItemId;

    const selectedDbLineItem = _lineItemId ? dbLineItems.find((dbLineItem) => dbLineItem.id === _lineItemId) : undefined;

    return { selectedDbLineItem, sourceLineItem };
  }, [objectInput, inputField, inputValue, dbLineItems]);

  const selectedTemplateValue = useMemo<LineItemSearchResult | undefined>(
    () =>
      selectedDbLineItem
        ? {
            id: selectedDbLineItem.id,
            originalLineItem: selectedDbLineItem.originalLineItem,
            elements: selectedDbLineItem.elements as LineItemElement[],
            category: selectedDbLineItem.category,
          }
        : undefined,
    [selectedDbLineItem],
  );

  if (isDbLineItemsLoading) {
    return (
      <div>
        Loading...
        <LoadingAnimation />
      </div>
    );
  }

  const showPartialSelection = !!inputField.parameters?.enablePartialSelection;
  const showVariableOverrides =
    !!inputField.parameters?.enableVariableOverride ||
    (!!selectedDbLineItem && sourceLineItem?.lineItemId === selectedDbLineItem.id && !!inputField.parameters?.canBeDifferentFromSource);
  const isLineItemSelectionEnabled = !!inputField.parameters?.sourceLineItemPath && !inputField.parameters?.canBeDifferentFromSource;
  const disablePermutationSelection = !!inputField.parameters?.disablePermutationSelection;
  const canBeDifferentFromSource = !!inputField.parameters?.canBeDifferentFromSource;
  const isPermutationSelectionEnabled = !disablePermutationSelection && !showPartialSelection && !showVariableOverrides;

  const onLineItemSearchSelect = (row: LineItemSearchResult | undefined) => {
    _onUpdate?.(
      row
        ? {
            lineItemId: row.id,
            // if we can set the permutation directly, then when the user selects a line item permutation from the template
            // selection dropdown, auto-set the permutation to whatever the user set
            ...(row.acceptedLineString && isPermutationSelectionEnabled ? { selectedPermutation: row.acceptedLineString } : {}),
          }
        : undefined,
    );
  };

  // bunch of args that we pass into each possible sub-component
  const variationProps = selectedDbLineItem
    ? {
        selectedDbLineItem,
        inputValue,
        sourceLineItem,
        canBeDifferentFromSource,
        onUpdate: (value: Partial<RuleInputLineItem> | undefined) => _onUpdate?.(value),
      }
    : undefined;

  return (
    <div className="flex flex-col gap-2">
      <LineItemPermutationSearch
        disabled={isLineItemSelectionEnabled}
        value={selectedTemplateValue}
        onSelect={onLineItemSearchSelect}
        placeholder="search line item permutations"
      />

      {variationProps && (
        <>
          {showPartialSelection && <PartialSelectionInput {...variationProps} />}
          {showVariableOverrides && <VariableOverrideInput {...variationProps} />}
          {isPermutationSelectionEnabled && <PermutationSelectInput {...variationProps} />}
        </>
      )}
    </div>
  );
};

/**
 * Lets the user pick an exact permutation of the selected line item. Clearing the permutation clears the line item value entirely.
 */
const PermutationSelectInput: React.FC<LineItemVariationProps> = ({ selectedDbLineItem, inputValue, onUpdate }) => {
  const { permutations, selectedPermutation } = useMemo(
    () => ({
      permutations: getAllValidPermutations(selectedDbLineItem.elements),
      selectedPermutation: inputValue?.selectedPermutation,
    }),
    [selectedDbLineItem, inputValue],
  );

  const { badgeByPermutation } = useLineItemPermutationCatSels(selectedDbLineItem.id);

  return (
    <PermutationCatSelSelect
      options={permutations ?? []}
      badgeByPermutation={badgeByPermutation}
      onSelect={(permutation) =>
        onUpdate(permutation ? { lineItemId: selectedDbLineItem.id, selectedPermutation: permutation } : undefined)
      }
      value={selectedPermutation}
      placeholder="select permutation"
    />
  );
};

/**
 * Lets the user pick a list of acceptable values for each variable element (a partial permutation). Values set by the source line item are shown as
 * inherited (and disabled) unless this line item is allowed to differ from its source.
 */
const PartialSelectionInput: React.FC<LineItemVariationProps> = ({
  selectedDbLineItem,
  inputValue,
  sourceLineItem,
  canBeDifferentFromSource,
  onUpdate,
}) => {
  const indexToSelectedValues = useMemo(() => {
    const result: IndexToSelectedValues = {};

    const inputSelectedValues = inputValue?.selectedValues ?? {};
    for (const [key, values] of Object.entries(inputSelectedValues)) {
      if (!!values) {
        result[key] = { values, isInherited: false };
      }
    }

    if (!canBeDifferentFromSource) {
      const sourceSelectedValues = sourceLineItem?.selectedValues ?? {};
      for (const [key, values] of Object.entries(sourceSelectedValues)) {
        if (!!values) {
          result[key] = { values, isInherited: true };
        }
      }
    }

    return result;
  }, [inputValue, sourceLineItem, canBeDifferentFromSource]);

  const onAcceptableValueUpdate = (index: number, values?: string[]) => {
    const selectedValues = { ...(inputValue?.selectedValues ?? {}) };
    if (values === undefined) {
      delete selectedValues[index.toString()];
    } else {
      selectedValues[index.toString()] = values;
    }

    onUpdate({ lineItemId: selectedDbLineItem.id, selectedValues });
  };

  return (
    <div className="flex flex-row items-start gap-2">
      {selectedDbLineItem.elements.map((element, elementIdx) =>
        element.type === "text" ? (
          <div key={element.value}>{element.value}</div>
        ) : (
          <MultiSelect
            key={`${JSON.stringify(element)}`}
            values={indexToSelectedValues[elementIdx.toString()]?.values ?? []}
            options={element.acceptableValues ?? []}
            disabled={indexToSelectedValues[elementIdx.toString()]?.isInherited}
            onUpdate={(values) => onAcceptableValueUpdate(elementIdx, values)}
          />
        ),
      )}
    </div>
  );
};

/**
 * Lets the user pick a single value for each variable element (a variable override). Values set by the source line item are shown as inherited (and
 * disabled) unless this line item is allowed to differ from its source.
 */
const VariableOverrideInput: React.FC<LineItemVariationProps> = ({
  selectedDbLineItem,
  inputValue,
  sourceLineItem,
  canBeDifferentFromSource,
  onUpdate,
}) => {
  const indexToSelectedSingleValues = useMemo(() => {
    const result: IndexToSelectedSingleValue = {};

    const inputSingleValues = inputValue?.variableOverrides ?? {};
    for (const [key, value] of Object.entries(inputSingleValues)) {
      if (!!value) {
        result[key] = { value, isInherited: false };
      }
    }

    if (!canBeDifferentFromSource) {
      const sourceSingleValues = sourceLineItem?.variableOverrides ?? {};
      for (const [key, value] of Object.entries(sourceSingleValues)) {
        if (!!value) {
          result[key] = { value, isInherited: true };
        }
      }
    }

    return result;
  }, [inputValue, sourceLineItem, canBeDifferentFromSource]);

  const onSingleValueUpdate = (index: number, value?: string) => {
    const variableOverrides = { ...(inputValue?.variableOverrides ?? {}) };
    if (value === undefined) {
      delete variableOverrides[index.toString()];
    } else {
      variableOverrides[index.toString()] = value;
    }

    onUpdate({ lineItemId: selectedDbLineItem.id, variableOverrides });
  };

  return (
    <div className="flex flex-row items-start gap-2">
      {selectedDbLineItem.elements.map((element, elementIdx) =>
        element.type === "text" ? (
          <div key={element.value}>{element.value}</div>
        ) : (
          <FilterableSelect
            key={`${JSON.stringify(element)}`}
            value={indexToSelectedSingleValues[elementIdx.toString()]?.value}
            options={element.acceptableValues ?? []}
            disabled={indexToSelectedSingleValues[elementIdx.toString()]?.isInherited}
            onSelect={(value) => onSingleValueUpdate(elementIdx, value)}
            placeholder={"inherited from parent"}
          />
        ),
      )}
    </div>
  );
};
